import { useState, FormEvent } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SearchInput, Button } from '@c2fo/liquidity';

export type SearchType = 'division' | 'user' | 'market';

const SEARCH_TYPE_LABELS: Record<SearchType, string> = {
  division: 'Division',
  user: 'User',
  market: 'Market',
};

const SEARCH_PLACEHOLDERS: Record<SearchType, string> = {
  division: 'Division / User / Market',
  user: 'Division / User / Market',
  market: 'Division / User / Market',
};

export interface AdminSearchBarProps {
  onSearch: (searchType: SearchType, query: string) => void;
}

export const AdminSearchBar = ({ onSearch }: AdminSearchBarProps) => {
  const [searchType, setSearchType] = useState<SearchType>('division');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    onSearch(searchType, query);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex max-w-md overflow-hidden rounded-lg bg-white shadow-sm focus-within:ring-2 focus-within:ring-primary-500/30 transition-shadow"
      role="search"
      aria-label="NavBar Search Form"
      data-testid="admin-search-bar"
    >
      <Select value={searchType} onValueChange={(value) => setSearchType(value as SearchType)}>
        <SelectTrigger
          size="sm"
          className="h-9 w-[7rem] shrink-0 rounded-none rounded-l-lg border-0 border-r border-stroke bg-gray-50 font-medium text-gray-700 shadow-none focus:ring-0 focus-visible:ring-0 data-[state=open]:rounded-l-lg data-[state=open]:border-stroke"
          aria-label="NavBar Search type"
          data-testid="search-type-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem key="division" value="division">
            {SEARCH_TYPE_LABELS.division}
          </SelectItem>
          <SelectItem key="user" value="user">
            {SEARCH_TYPE_LABELS.user}
          </SelectItem>
          <SelectItem key="market" value="market">
            {SEARCH_TYPE_LABELS.market}
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="flex-1 min-w-0 mr-1.5 [&_input]:h-9 [&_input]:rounded-none [&_input]:border-0 [&_input]:shadow-none [&_input]:text-gray-900 [&_input]:placeholder:text-gray-500 focus-within:[&_input]:ring-0">
        <SearchInput
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          placeholder={SEARCH_PLACEHOLDERS[searchType]}
          size="sm"
          className="w-full max-w-80 min-w-0"
          aria-label="NavBar Division, User, or Market search input"
          withSearchIcon
          onClear={() => setSearchQuery('')}
        />
      </div>
      <Button
        type="submit"
        variant="primary"
        size="sm"
        disabled={!searchQuery.trim()}
        className="h-9 shrink-0 rounded-none rounded-r-lg px-4 before:opacity-0 focus-visible:before:opacity-0 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-0"
        aria-label="NavBar Search button"
      >
        Search
      </Button>
    </form>
  );
};
